import React from 'react';
import { Loader } from 'arag-ui-lib';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import { useFormikContext } from 'formik';
import codes from '../../utils/constants/codes';
import config from '../../utils/constants/config';
import AttachmentItem from '../attachmentItem/attachmentItem';
import QuestionDocumentTypes from '../questionDocumentTypes/questionDocumentTypes';
import applicationSettingsProjections from '../../projections/applicationSettings';
import FormikFieldWrapper from '../general/formikFieldWrapper/formikFieldWrapper';
import { useAppSelector } from '../../hooks/hooks';
import { IsUploadingFile } from '../../utils/constants/interfaces';
import { useTranslation } from 'react-i18next';

const extractUniqueSelectedDocumentTypesThatCanBeUploadedOnlyOnce = (
    attachments: any[],
) => {
    if (!attachments) {
        return [];
    }
    return attachments
        .filter(attachment => attachment.documentTypes)
        .reduce((acc, curr) => {
            const uniqueAndNotAdded = curr.documentTypes
                .filter(
                    (type: {
                        item: {
                            code: string;
                            uploadOnlyOnePerClaimApplication: boolean;
                        };
                    }) =>
                        type.item &&
                        acc.includes(type.item.code) === false &&
                        type.item.uploadOnlyOnePerClaimApplication,
                )
                .map((unique: { item: { code: string } }) => unique.item.code);
            return [...acc, ...uniqueAndNotAdded];
        }, []);
};

const Attachments = (props: {
    onAutoSaveDraftClaim: (values: any, errors: any) => any;
    isUploadingFile: IsUploadingFile | null;
    setUploadingfile: Function;
    setFileUploadingProgress: any;
    fileUploadingProgress: any;
    setDownloadingFile: Function;
    isDownloadingFile: string | null;
    setFileDownloadingProgress: Function;
    fileDownloadingProgress: any;
}) => {
    const {
        onAutoSaveDraftClaim,
        isUploadingFile,
        setUploadingfile,
        setFileUploadingProgress,
        fileUploadingProgress,
        setDownloadingFile,
        isDownloadingFile,
        setFileDownloadingProgress,
        fileDownloadingProgress,
    } = props;
    const { t } = useTranslation();
    const { values, setFieldValue, errors, setFieldError }: any =
        useFormikContext();
    const maxAttachmentsSizeInBytes = useAppSelector(
        applicationSettingsProjections.maxAttachmentsSizeInBytes,
    ) as number | null;
    const attachments =
        values && values[codes.claimAttachments]
            ? values[codes.claimAttachments]
            : [];

    const asnwerValues: Array<any> =
        values && Object.keys(values).length > 0 ? Object.values(values) : [];
    const uploadOnlyOnceDocumentTypesCodes =
        extractUniqueSelectedDocumentTypesThatCanBeUploadedOnlyOnce(
            attachments,
        );
    const question = useAppSelector(
        state => state.question.question.byId['claim-attachments'],
    );

    const addAttachments = async (
        newAttachments: any[],
        rejectedFileIndexes: Array<number>,
    ) => {
        if (rejectedFileIndexes.length > 0) {
            newAttachments = newAttachments.filter(
                (x, index) => !rejectedFileIndexes.includes(index),
            );
        }
        setUploadingfile(true);
        let uploadedAttachments: any[] = [];
        if (newAttachments.length > 0) {
            for (const attachment of newAttachments) {
                let uploadedAttachment =
                    await GENERAL_FUNCTIONS.requestToUploadFileToBlob({
                        fileName: attachment.name,
                        type: codes.attachmentTypeEnum.ClaimFormQuestion,
                    });
                // Create a new AbortController instance
                const abortController = new AbortController();
                //  //2.1  here add additional data to object
                const fileSize = attachment.file?.size;
                const fileToMB = Math.round(fileSize / 1024);
                if (uploadedAttachment) {
                    uploadedAttachments = [
                        ...uploadedAttachments,
                        {
                            ...attachment,
                            id: uploadedAttachment.attachmentId,
                            abortController: abortController,
                            fileToMB: fileToMB,
                            uploadedAttachment: uploadedAttachment,
                            hasMetaDataSet: false,
                        },
                    ];
                }
            }
        }
        const updatedList = [...attachments, ...uploadedAttachments];
        setFieldValue(codes.claimAttachments, updatedList);
        setUploadingfile(null);
        await fileUploadingProgressHandler(uploadedAttachments);
    };

    const fileUploadingProgressHandler = async (
        updatedList: {
            uploadedAttachment: { size: number };
            file: any;
            abortController: any;
            hasMetaDataSet: boolean;
        }[],
    ) => {
        let uploadedAttachmentsProgress: any[] = [];
        //2. Handle promises of each progress bar of file uploading and update the state
        const uploadPromises = updatedList.map(
            async (item: {
                hasMetaDataSet: boolean;
                uploadedAttachment: { size: number };
                file: any;
                abortController: any;
            }) => {
                const progressPromise = new Promise<void>(resolve => {
                    GENERAL_FUNCTIONS.fileUploadAzureBlob(
                        item.uploadedAttachment,
                        item.file,
                        (
                            progress: number,
                            id: string,
                            hasMetaDataSet: boolean,
                        ) => {
                            // Check if the file has been removed from the list before updating progress
                            setFileUploadingProgress((prevProgress: any[]) => {
                                const updatedProgress = {
                                    id,
                                    progress,
                                    hasMetaDataSet,
                                };
                                const index = prevProgress.findIndex(
                                    item => item.id === id,
                                );
                                if (index !== -1) {
                                    return [
                                        ...prevProgress.slice(0, index),
                                        updatedProgress,
                                        ...prevProgress.slice(index + 1),
                                    ];
                                } else {
                                    return [...prevProgress, updatedProgress];
                                }
                            });
                        },
                        item.abortController,
                        item.hasMetaDataSet,
                    ).then(() => resolve());
                });
                //check if the file has been removed from the list before updating progress
                const index = updatedList.findIndex(
                    fileItem => fileItem === item,
                );
                if (index !== -1 || !item.abortController.signal.aborted) {
                    uploadedAttachmentsProgress.push({
                        ...item,
                    });
                }
                await progressPromise;
            },
        );

        await Promise.all(uploadPromises);
        setUploadingfile(null);
    };

    const updateAttachment = (
        updatedAttachment: any,
        updatedAttachmentIndex: number,
    ) => {
        const updatedList = attachments.map(
            (attachment: any, index: number) => {
                return updatedAttachmentIndex === index
                    ? updatedAttachment
                    : attachment;
            },
        );
        setFieldValue(codes.claimAttachments, updatedList);
    };

    const removeAttachment = async (
        indexOfAttachmentToBeRemoved: number | any,
    ) => {
        setFileUploadingProgress((prevProgress: any[]) =>
            prevProgress.filter(
                item =>
                    item.id !== attachments[indexOfAttachmentToBeRemoved].id,
            ),
        );
        const updatedList = attachments.filter(
            (a: any, index: number) => index !== indexOfAttachmentToBeRemoved,
        );
        setFieldValue(codes.claimAttachments, updatedList);
        setUploadingfile(null);
        await GENERAL_FUNCTIONS.requestToRemoveFile(
            attachments[indexOfAttachmentToBeRemoved].id,
        );
    };
    const cancelUpload = async (
        fileId: string,
        indexOfAttachmentToBeRemoved: number,
    ) => {
        // Find the file with the corresponding fileId
        const file = attachments.find(
            (item: { id: string }) => item.id === fileId,
        );
        // Remove the file from the progress array
        if (file && file.abortController) {
            // Cancel the file upload by aborting the controller signal
            file.abortController.abort();
            setFileUploadingProgress((prevProgress: any[]) =>
                prevProgress.filter(
                    item =>
                        item.id !==
                        attachments[indexOfAttachmentToBeRemoved].id,
                ),
            );
            // Remove the file from the form values
        }
        const updatedList = attachments.filter(
            (a: any, index: number) => index !== indexOfAttachmentToBeRemoved,
        );
        setFieldValue(codes.claimAttachments, updatedList);
        await GENERAL_FUNCTIONS.requestToRemoveFile(
            attachments[indexOfAttachmentToBeRemoved].id,
        );
        setUploadingfile(null);
    };
    const autoSaveAction = () => {
        onAutoSaveDraftClaim(values, errors);
    };
    const handleTypeChange =
        (attachmentIndex: any) =>
        (values: any): any => {
            const updatedAttachment = {
                ...attachments[attachmentIndex],
                documentTypes: values,
            };
            updateAttachment(updatedAttachment, attachmentIndex);
        };
    const handleDownload = async (
        attachment: { id: string; name: string } | any,
    ) => {
        setDownloadingFile(attachment.id);

        const blob = await GENERAL_FUNCTIONS.requestToDownloadFile(
            attachment.id,
            attachment.name,
            codes.attachment,
            (id: string, progress: number) => {
                setFileDownloadingProgress(
                    (
                        prevProgress: (
                            any | { name: string; progress: number }
                        )[],
                    ) => {
                        // Find the index of the progress object with the matching fileId
                        const index = prevProgress.findIndex(
                            item => item.id === id,
                        );
                        // Create a new progress object with the updated progress value
                        const updatedProgress = { id: id, progress: progress };
                        // Update the progress array by replacing the old object with the updated one,
                        // or appending the updated object if it doesn't exist yet
                        if (index !== -1) {
                            return [
                                ...prevProgress.slice(0, index),
                                updatedProgress,
                                ...prevProgress.slice(index + 1),
                            ];
                        } else {
                            return [...prevProgress, updatedProgress];
                        }
                    },
                );
            },
        );

        if (blob && blob !== codes.warnings && blob !== codes.fileNotFound) {
            GENERAL_FUNCTIONS.downloadURI(blob, attachment.name);
            setDownloadingFile(null);
            setFileDownloadingProgress((prevProgress: any[]) => [
                ...prevProgress.filter(item => item.id !== attachment.id),
            ]);
        }
        if (blob === codes.warnings) {
            setFileDownloadingProgress((prevProgress: any[]) => [
                ...prevProgress.filter(item => item.id !== attachment.id),
            ]);
            setDownloadingFile(null);
        }
        if (blob === codes.fileNotFound) {
            setDownloadingFile(null);
        }
    };
    const handleDrop = async (files: any[], rejectedFile: string | any[]) => {
        let rejectedFileIndexes: Array<number> = [];
        if (rejectedFile && rejectedFile.length > 0) {
            setFieldError(
                codes.dragDrop,
                ` File type ${GENERAL_FUNCTIONS.validateFileExtensions(rejectedFile[0].name)} is not supported`,
            );
        }
        const readFiles = await Promise.all(
            files.map(async (file, index) => {
                let rejectedFileIndex = GENERAL_FUNCTIONS.sizeOfFiles(file, {
                    option: 0,
                    setFieldError: setFieldError,
                    formikBag: null,
                    field: codes.dragDrop,
                    rejectedFile: rejectedFile,
                });
                if (rejectedFileIndex === 1) {
                    rejectedFileIndexes = [...rejectedFileIndexes, index];
                }

                return {
                    file: file,
                    name: file.name,
                    hasMetaDataSet: false,
                };
            }),
        );
        await addAttachments(readFiles, rejectedFileIndexes);
    };

    return (
        <div>
            <QuestionDocumentTypes
                questionCode={codes.claimAttachments}
                asnwerValues={asnwerValues}
            />
            <div className={'form-group'}>
                <div className={'col-sm-12'}>
                    <FormikFieldWrapper
                        inputSize={'col-sm-12'}
                        variant={codes.dragDrop}
                        // @ts-ignore
                        validator={GENERAL_FUNCTIONS.validationCreatorForAttachments(
                            question,
                        )(values, errors)}
                        customErrorMessage={
                            errors &&
                            errors[codes.dragDrop] &&
                            question &&
                            errors[question.code]
                                ? errors[codes.dragDrop]
                                : errors &&
                                    question &&
                                    errors[question.code] &&
                                    !errors[codes.dragDrop]
                                  ? errors[question.code]
                                  : null
                        }
                        getInputProps={() => ({
                            name: codes.claimAttachments,
                            accepts: config.ACCEPTED_FILE_EXTENSIONS,
                            allowMultiple: true,
                            onDrop: (
                                items: any[],
                                rejectedFile: string | any[],
                            ) => handleDrop(items, rejectedFile),
                            label: (
                                <div>
                                    <div>{t('dragAndDrop') as string}</div>
                                    <div style={{ fontWeight: 'normal' }}>
                                        {t('maxFileSize') as string}
                                    </div>
                                    {isUploadingFile && (
                                        <Loader
                                            variant={'inline'}
                                            label={t('uploadingFile')}
                                        />
                                    )}
                                </div>
                            ),
                        })}
                        inputProps={{
                            name: codes.claimAttachments,
                        }}
                        label={''}
                    />
                </div>
            </div>
            <div className={'form-horizontal'}>
                {attachments.map(
                    (
                        attachment:
                            { documentTypes: any[]; name: string } | any,
                        index: React.Key | null | number,
                    ) => (
                        <AttachmentItem
                            key={index}
                            attachment={attachment}
                            handleTypeChange={handleTypeChange}
                            uploadOnlyOnceDocumentTypesCodes={
                                uploadOnlyOnceDocumentTypesCodes
                            }
                            attachmentIndex={index}
                            handleDownload={handleDownload}
                            removeAttachment={removeAttachment}
                            maxAttachmentsSizeInBytes={
                                maxAttachmentsSizeInBytes
                            }
                            autoSaveAction={autoSaveAction}
                            fileUploadingProgress={fileUploadingProgress}
                            cancelUpload={cancelUpload}
                            fileDownloadingProgress={fileDownloadingProgress}
                            isDownloadingFile={isDownloadingFile}
                        />
                    ),
                )}
            </div>
        </div>
    );
};

export default Attachments;
